import { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Loading from '@/components/loading'
import { Sensor } from '@/features/dashboard/models'
import Table from '@/features/dashboard/components/dashboardTable/Table'
import TableBar, { SORT_OPTIONS, TableBarRef } from '@/features/dashboard/components/dashboardTable/TableBar'
import { SensorCards } from '@/features/dashboard/components/sensorCards'
import {
    useGetOnboardedSensorsPage,
    usePrefetchOnboardedSensorsPage,
} from '@/core/hooks-and-services/onboardedSensorsService'
import { OnboardedSensorsPageParams } from '@/api/apiService'
import {
    InstalledSensorStatus,
    SortItem,
    StidTagsResponseModel,
    BatteryStatus,
    SignalQuality,
} from '@/core/models/types'
import { isCardViewAtom, scannedSerialNumAtom, selectedSensorToEditAtom } from '@/core/store/globalStore'
import { useAtom } from 'jotai'
import styled from 'styled-components'
import { Search, Button, Icon } from '@equinor/eds-core-react'
import { searchAtom, pageIndexDashboardAtom } from '@/core/store/globalStore'
import Scanner from '../Scanner'
import { camera } from '@equinor/eds-icons'
import ChipRow from './ChipRow'
import ViewFilteredSensorsInEchoButton from '@/components/viewFilteredSensorsInEchoButton/ViewFilteredSensorsInEchoButton'
import { useDebouncedValue } from '@/hooks'
import { EmptyStateBanner } from '@/features/dashboard/components/Banner/EmptyStateBanner'

interface DashboardTableProps {
    setScannerFetchingData?: (value: boolean | ((prevState: boolean) => boolean)) => void
}

const DashboardTable: React.FC<DashboardTableProps> = ({ setScannerFetchingData }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const [search, setSearch] = useAtom(searchAtom)
    const [currentPageIndex, setCurrentPageIndex] = useAtom(pageIndexDashboardAtom)
    const [selectedSensorToEdit, setSelectedSensorToEdit] = useAtom(selectedSensorToEditAtom)
    const [isCardView, setIsCardView] = useAtom(isCardViewAtom)

    const [, setScannedSerialNumber] = useAtom(scannedSerialNumAtom)

    const handleScan = useCallback(
        (payload: any) => {
            setScannedSerialNumber(payload)
        },
        [setScannedSerialNumber]
    )

    const onEditSensor = useCallback((sensor: Sensor) => {
        if (tableBarRef.current) {
            tableBarRef.current.onEditSensor(sensor)
        }
    }, [])

    const [filter, setFilter] = useState<{
        status: (InstalledSensorStatus | string)[]
        plant: string
        project: string
        device: string
        protocol: string
        system: string
        area: string
        superiorTag: string
        sensorType: string
        batteryStatus: BatteryStatus | ''
        signalQuality: SignalQuality | ''
        tags: StidTagsResponseModel[]
    }>({
        status: [],
        plant: '',
        project: '',
        device: '',
        protocol: '',
        system: '',
        area: '',
        superiorTag: '',
        sensorType: '',
        batteryStatus: '',
        signalQuality: '',
        tags: [],
    })

    const [sortSpecification, setSortSpecification] = useState<{
        tag: SortItem
        installationDate: SortItem
        device: SortItem
        sensorStatus: SortItem
        batteryCapacity: SortItem
        signalStrength: SortItem
    }>({
        tag: { ...SORT_OPTIONS.tag, selected: null },
        installationDate: SORT_OPTIONS.installationDate,
        device: { ...SORT_OPTIONS.device, selected: null },
        sensorStatus: { ...SORT_OPTIONS.sensorStatus, selected: null },
        batteryCapacity: { ...SORT_OPTIONS.batteryCapacity, selected: null },
        signalStrength: { ...SORT_OPTIONS.signalStrength, selected: null },
    })

    const handleFilter = (filters: {
        status: (InstalledSensorStatus | string)[]
        plant: string
        project: string
        device: string
        protocol: string
        system: string
        area: string
        superiorTag: string
        sensorType: string
        batteryStatus: BatteryStatus | ''
        signalQuality: SignalQuality | ''
        tags: StidTagsResponseModel[]
    }) => {
        setFilter({
            status: filters.status,
            plant: filters.plant,
            project: filters.project,
            device: filters.device,
            protocol: filters.protocol,
            system: filters.system,
            area: filters.area,
            superiorTag: filters.superiorTag,
            sensorType: filters.sensorType,
            batteryStatus: filters.batteryStatus,
            signalQuality: filters.signalQuality,
            tags: filters.tags,
        })
        setCurrentPageIndex(1)
    }

    const [windowWidth, setWindowWidth] = useState(window.innerWidth)
    const [windowHeight, setWindowHeight] = useState(window.innerHeight)
    const tableBarRef = useRef<TableBarRef>(null)

    const openAddSensorModal = (location.state as { openAddSensorModal?: boolean } | null)?.openAddSensorModal

    useEffect(() => {
        let frame = 0
        const handleResize = () => {
            if (frame) return
            frame = window.requestAnimationFrame(() => {
                frame = 0
                setWindowWidth(window.innerWidth)
                setWindowHeight(window.innerHeight)
            })
        }
        window.addEventListener('resize', handleResize)
        return () => {
            if (frame) window.cancelAnimationFrame(frame)
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    useEffect(() => {
        if (!openAddSensorModal) return

        tableBarRef.current?.openAddSensor()
        navigate(location.pathname, { replace: true, state: null })
    }, [location.pathname, navigate, openAddSensorModal])

    useEffect(() => {
        if (selectedSensorToEdit && tableBarRef.current) {
            tableBarRef.current.onEditSensor(selectedSensorToEdit)
            setSelectedSensorToEdit(null)
        }
    }, [selectedSensorToEdit, setSelectedSensorToEdit])

    const pageSize = useMemo(() => {
        const isMobile = windowWidth <= 1024
        if (!isCardView && !isMobile) return 250
        if (isMobile) return 10
        const cardWidth = 414
        const cardHeight = 212
        const padding = 8
        const gap = 8
        const spaceAroundW = 242
        const spaceAroundH = 250
        const totalW = cardWidth + 2 * padding + gap
        const totalH = cardHeight + 2 * padding + gap
        const containerW = windowWidth - spaceAroundW + gap
        const containerH = windowHeight - spaceAroundH + gap
        const cols = Math.floor(containerW / totalW)
        const rows = Math.floor(containerH / totalH)
        const calculated = rows * cols
        return Math.max(1, calculated || cols || 1)
    }, [windowWidth, windowHeight, isCardView])

    const debouncedSearch = useDebouncedValue(search, 300)

    const pagedParams = useMemo<OnboardedSensorsPageParams>(() => {
        let sortBy: string | undefined
        let sortDir: 'asc' | 'desc' | undefined
        if (sortSpecification.tag.selected) {
            sortBy = 'tag'
            sortDir = sortSpecification.tag.selected === SORT_OPTIONS.tag.first ? 'asc' : 'desc'
        } else if (sortSpecification.installationDate.selected) {
            sortBy = 'installationDate'
            sortDir =
                sortSpecification.installationDate.selected === SORT_OPTIONS.installationDate.first ? 'desc' : 'asc'
        } else if (sortSpecification.device.selected) {
            sortBy = 'device'
            sortDir = sortSpecification.device.selected === SORT_OPTIONS.device.first ? 'asc' : 'desc'
        } else if (sortSpecification.sensorStatus.selected) {
            sortBy = 'sensorStatus'
            sortDir = sortSpecification.sensorStatus.selected === SORT_OPTIONS.sensorStatus.first ? 'asc' : 'desc'
        } else if (sortSpecification.batteryCapacity.selected) {
            sortBy = 'batteryCapacity'
            sortDir = sortSpecification.batteryCapacity.selected === SORT_OPTIONS.batteryCapacity.first ? 'asc' : 'desc'
        } else if (sortSpecification.signalStrength.selected) {
            sortBy = 'signalStrength'
            sortDir = sortSpecification.signalStrength.selected === SORT_OPTIONS.signalStrength.first ? 'asc' : 'desc'
        }
        return {
            page: currentPageIndex,
            pageSize,
            search: debouncedSearch ? debouncedSearch : undefined,
            status: filter.status.length > 0 ? filter.status.map(String) : undefined,
            plant: filter.plant || undefined,
            project: filter.project || undefined,
            area: filter.area || undefined,
            device: filter.device || undefined,
            protocol: filter.protocol || undefined,
            sensorType: filter.sensorType || undefined,
            superiorTag: filter.superiorTag || undefined,
            system: filter.system || undefined,
            batteryStatus: filter.batteryStatus || undefined,
            signalQuality: filter.signalQuality || undefined,
            sortBy,
            sortDir,
        }
    }, [currentPageIndex, pageSize, debouncedSearch, filter, sortSpecification])

    const pagedResult = useGetOnboardedSensorsPage(pagedParams)

    const data = pagedResult.data
    const isLoading = pagedResult.isLoading
    const error = pagedResult.error
    const totalCount = pagedResult.totalCount
    const refetchSensors = pagedResult.refetch

    const nextPageParams = useMemo<OnboardedSensorsPageParams | null>(() => {
        if (totalCount == null) return null
        const totalPages = Math.ceil(totalCount / pageSize)
        if (currentPageIndex >= totalPages) return null
        return { ...pagedParams, page: currentPageIndex + 1 }
    }, [pagedParams, currentPageIndex, pageSize, totalCount])
    usePrefetchOnboardedSensorsPage(nextPageParams)

    const handleIsCardView = () => {
        setIsCardView((prev) => !prev)
    }

    const handleOnSearchValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setSearch(value)
        setScannedSerialNumber(null)
        setCurrentPageIndex(1)
    }

    const cardViewButtonIcon = isCardView ? 'list' : 'grid_on'

    const pageRows = data ?? []

    const hasActiveFilters = Object.entries({ ...filter, search: debouncedSearch }).some(
        ([key, value]) => key !== 'plant' && (Array.isArray(value) ? value.length > 0 : Boolean(value))
    )

    const isEmpty = !isLoading && totalCount === 0 && pageRows.length === 0

    if (isLoading) {
        return <Loading />
    }

    // A failed refetch while offline must not tear down the table when cached sensors are available.
    if (error !== null && data.length === 0) {
        throw error
    }

    const ScannerButton = (
        <ScannerStyledButton color="secondary">
            <StyledIcon data={camera} size={24} />
            Scan sensor
        </ScannerStyledButton>
    )

    return (
        <div>
            {windowWidth <= 1024 && (
                <MobileContainer>
                    <ButtonGroup>
                        <Search
                            aria-label="Search"
                            placeholder="Search"
                            onChange={handleOnSearchValueChange}
                            value={search || ''}
                        ></Search>
                        <Scanner
                            onScan={handleScan}
                            onFetchingData={setScannerFetchingData}
                            scannerButton={ScannerButton}
                            isReadSensor={true}
                        />
                    </ButtonGroup>
                    <ViewFilteredSensorsInEchoButton filteredSensors={pageRows} />
                    <ChipRow />
                </MobileContainer>
            )}
            <TableBar
                cardViewButtonIcon={cardViewButtonIcon}
                onFilter={handleFilter}
                onToggle={handleIsCardView}
                handleScan={handleScan}
                sensors={data ?? []}
                filteredSensors={pageRows}
                sortSpecification={sortSpecification}
                setSortSpecification={setSortSpecification}
                ref={tableBarRef}
            />
            {isEmpty ? (
                <EmptyStateBanner
                    title={hasActiveFilters ? 'No results found' : 'No sensors available'}
                    message={
                        hasActiveFilters
                            ? 'No sensors match your current search or filter criteria. Try adjusting or clearing your filters to see more results.'
                            : 'There are no sensors to display yet. Once sensors are added, they will appear here.'
                    }
                />
            ) : windowWidth <= 1024 ? (
                <>
                    <SensorCards
                        filteredSensors={pageRows}
                        onRefetchSensors={refetchSensors}
                        isFetchingSensors={isLoading}
                        sortSpecification={sortSpecification}
                        onEditSensor={onEditSensor}
                        totalItems={totalCount}
                        serverPaginated={true}
                        pageSize={pageSize}
                    />
                </>
            ) : (
                <>
                    {isCardView ? (
                        <>
                            <SensorCards
                                filteredSensors={pageRows}
                                onRefetchSensors={refetchSensors}
                                isFetchingSensors={isLoading}
                                sortSpecification={sortSpecification}
                                onEditSensor={onEditSensor}
                                totalItems={totalCount}
                                serverPaginated={true}
                                pageSize={pageSize}
                            />
                        </>
                    ) : (
                        <>
                            <Table
                                filteredSensors={pageRows}
                                onRefetchSensors={refetchSensors}
                                sortSpecification={sortSpecification}
                                onEditSensor={onEditSensor}
                                totalItems={totalCount}
                                serverPaginated={true}
                                pageSize={pageSize}
                            />
                        </>
                    )}
                </>
            )}
        </div>
    )
}

const MobileContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin: 0 0 1rem 0;
    align-items: center;
`
const ButtonGroup = styled.div`
    display: flex;
    gap: 0.5rem;
    justify-content: flex-start;
`
const ScannerStyledButton = styled(Button)`
    width: 144px;
    height: 38px;
    padding: 0px;
`

const StyledIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    width: 24px;

    &:hover {
        color: #1c1c1c;
        background: #f7f7f7;
    }

    @media (max-width: 1024px) {
        &:hover {
            background: #243746;
            color: white;
        }
    }
`

export default DashboardTable
